import { toast } from 'sonner'
import { api } from '@/lib/api/client'
import { openPrintDocument, type PrintSection } from '@/features/hr/personel/lib/fileExport'
import i18n from '@/i18n'
import { fmtDate, fmtMoney } from '../shared/format'
import { downloadCsv } from './csv'
import type { CashFlow, Overview, TaxReport, Transaction } from './types'
import { monthLabel } from './ui'

/**
 * Finans dışa aktarımları. PDF tarayıcının yazdır penceresiyle üretilir ("PDF olarak kaydet"),
 * bordro ve dekontla aynı şablonu kullanır. Tutarlar sunucudan gelen değerlerdir; istemci hesap yapmaz.
 */

const t = (k: string, o?: Record<string, unknown>) => i18n.t(k, o)
const csvNum = (v: string) => Number(v || 0).toFixed(2).replace('.', ',')
const stamp = () => new Date().toISOString().slice(0, 10)

function print(doc: { title: string; subtitle?: string; sections: PrintSection[]; note?: string }) {
  if (!openPrintDocument(doc)) toast.error(t('finance.reports.popupBlocked'))
}

type Lookups = { partyName: (id: string) => string; catName: (id: string) => string }

const txRow = (x: Transaction, l: Lookups) => [
  fmtDate(x.date),
  t(`finance.kind.${x.kind}`),
  l.partyName(x.counterpartyId),
  l.catName(x.categoryId),
  x.docNo || '—',
  x.description,
]

export function exportTransactionsCsv(rows: Transaction[], l: Lookups) {
  downloadCsv(
    `gelir-gider-${stamp()}.csv`,
    [t('finance.reports.cols.date'), t('finance.reports.cols.kind'), t('finance.reports.cols.party'), t('finance.reports.cols.category'), t('finance.reports.cols.doc'), t('finance.reports.cols.description'), t('finance.reports.cols.net'), t('finance.reports.cols.vat'), t('finance.reports.cols.withholding'), t('finance.reports.cols.total'), t('finance.reports.cols.remaining')],
    rows.map((x) => [...txRow(x, l), csvNum(x.net), csvNum(x.vat), csvNum(x.withholding), csvNum(x.total), csvNum(x.remaining)]),
  )
}

export function printTransactions(rows: Transaction[], l: Lookups, subtitle: string) {
  const sum = (k: 'total' | 'remaining', kind: Transaction['kind']) => rows.filter((x) => x.kind === kind).reduce((n, x) => n + Number(x[k] || 0), 0).toFixed(2)
  print({
    title: t('finance.reports.transactionsTitle'),
    subtitle,
    sections: [
      {
        table: {
          head: [t('finance.reports.cols.date'), t('finance.reports.cols.kind'), t('finance.reports.cols.party'), t('finance.reports.cols.category'), t('finance.reports.cols.doc'), t('finance.reports.cols.description'), t('finance.reports.cols.total'), t('finance.reports.cols.remaining')],
          rows: rows.map((x) => [...txRow(x, l), fmtMoney(x.total), fmtMoney(x.remaining)]),
          numeric: [6, 7],
        },
      },
      {
        heading: t('finance.reports.totals'),
        rows: [
          [t('finance.reports.incomeTotal'), fmtMoney(sum('total', 'income'))],
          [t('finance.reports.expenseTotal'), fmtMoney(sum('total', 'expense'))],
          [t('finance.reports.receivable'), fmtMoney(sum('remaining', 'income'))],
          [t('finance.reports.payable'), fmtMoney(sum('remaining', 'expense'))],
        ],
      },
    ],
    note: t('finance.reports.count', { count: rows.length }),
  })
}

export function exportCashFlowCsv(cf: CashFlow) {
  downloadCsv(
    `nakit-akisi-${stamp()}.csv`,
    [t('finance.reports.cols.month'), t('finance.reports.cols.inflow'), t('finance.reports.cols.outflow'), t('finance.reports.cols.net'), t('finance.reports.cols.closing')],
    cf.months.map((m) => [m.month, csvNum(m.inflow), csvNum(m.outflow), csvNum(m.net), csvNum(m.closing)]),
  )
}

export function printCashFlow(cf: CashFlow, month: string) {
  print({
    title: t('finance.reports.cashFlowTitle'),
    subtitle: monthLabel(month),
    sections: [
      {
        table: {
          head: [t('finance.reports.cols.month'), t('finance.reports.cols.inflow'), t('finance.reports.cols.outflow'), t('finance.reports.cols.net'), t('finance.reports.cols.closing')],
          rows: cf.months.map((m) => [monthLabel(m.month), fmtMoney(m.inflow), fmtMoney(m.outflow), fmtMoney(m.net), fmtMoney(m.closing)]),
          numeric: [1, 2, 3, 4],
        },
      },
      {
        heading: t('finance.reports.projection'),
        rows: [
          [t('finance.reports.cash'), fmtMoney(cf.cash)],
          [t('finance.reports.cols.inflow'), fmtMoney(cf.projected.inflow)],
          [t('finance.reports.cols.outflow'), fmtMoney(cf.projected.outflow)],
        ],
        total: [t('finance.reports.projectedClosing'), fmtMoney(cf.projected.closing)],
      },
    ],
  })
}

export type PayrollCost = { gross: string; net: string } | null

/** Aylık yönetim özeti: tahakkuk sonucu, nakit, alacak/borç, personel maliyeti ve vergi yükü tek sayfada. */
export function printManagementSummary(o: Overview, tax: TaxReport | undefined, payroll: PayrollCost, catName: (id: string) => string) {
  const sections: PrintSection[] = [
    {
      heading: t('finance.reports.accrual'),
      rows: [
        [t('finance.reports.incomeTotal'), fmtMoney(o.accrual.income.value)],
        [t('finance.reports.expenseTotal'), fmtMoney(o.accrual.expense.value)],
      ],
      total: [t('finance.reports.result'), fmtMoney(o.accrual.result.value)],
    },
    {
      heading: t('finance.reports.cashSection'),
      rows: [
        [t('finance.reports.collected'), fmtMoney(o.collected.value)],
        [t('finance.reports.paidOut'), fmtMoney(o.paidOut.value)],
        [t('finance.reports.receivable'), fmtMoney(o.receivable.value)],
        [t('finance.reports.payable'), fmtMoney(o.payable.value)],
      ],
      total: [t('finance.reports.netCash'), fmtMoney(o.netCash.value)],
    },
    {
      heading: t('finance.reports.payroll'),
      rows: payroll
        ? [
            [t('finance.reports.payrollGross'), fmtMoney(payroll.gross)],
            [t('finance.reports.payrollNet'), fmtMoney(payroll.net)],
          ]
        : [[t('finance.reports.payrollNone'), '—']],
    },
    {
      heading: t('finance.reports.tax'),
      rows: [
        [t('finance.reports.vatPayable'), fmtMoney(o.tax.vat)],
        [t('finance.reports.withholding'), fmtMoney(o.tax.withholding)],
        [t('finance.reports.corporate'), fmtMoney(o.tax.corporate)],
        ...(tax ? ([[t('finance.reports.missingDocs'), String(tax.docCheck.missing)]] as [string, string][]) : []),
      ],
    },
  ]
  if (o.categoryDetail.length) {
    sections.push({
      heading: t('finance.reports.byCategory'),
      table: {
        head: [t('finance.reports.cols.category'), t('finance.reports.cols.total'), t('finance.reports.cols.missingDoc')],
        rows: o.categoryDetail.map((c) => [catName(c.categoryId), fmtMoney(c.amount), fmtMoney(c.missing)]),
        numeric: [1, 2],
      },
    })
  }
  print({ title: t('finance.reports.managementTitle'), subtitle: monthLabel(o.period), sections, note: t('finance.reports.managementNote') })
}

/** Mizan satırı: devir (önceki dönemler), dönem, toplam (kümülatif) ve bakiye; üst hesap (group) alt hesapların toplamı. */
export type TrialRow = {
  code: string
  name: string
  level: number
  group: boolean
  openingDebit: string
  openingCredit: string
  periodDebit: string
  periodCredit: string
  totalDebit: string
  totalCredit: string
  balanceDebit: string
  balanceCredit: string
}
export type TrialBalance = { rows: TrialRow[]; totals: TrialRow; balanced: boolean }

export const TRIAL_COLUMNS = ['openingDebit', 'openingCredit', 'periodDebit', 'periodCredit', 'totalDebit', 'totalCredit', 'balanceDebit', 'balanceCredit'] as const
const trialHead = () => [t('finance.books.code'), t('finance.books.account'), ...TRIAL_COLUMNS.map((c) => t(`finance.books.cols.${c}`))]

export function exportTrialBalanceCsv(tb: TrialBalance, period: string) {
  downloadCsv(
    `mizan-${period}.csv`,
    trialHead(),
    [...tb.rows, tb.totals].map((r) => [r.code, r.name, ...TRIAL_COLUMNS.map((c) => csvNum(r[c]))]),
  )
}

export function printTrialBalance(tb: TrialBalance, period: string) {
  print({
    title: t('finance.books.trialTitle'),
    subtitle: monthLabel(period),
    sections: [
      {
        table: {
          head: trialHead(),
          rows: tb.rows.map((r) => [r.code, `${'  '.repeat(r.level)}${r.name}`, ...TRIAL_COLUMNS.map((c) => fmtMoney(r[c]))]),
          numeric: [2, 3, 4, 5, 6, 7, 8, 9],
          foot: [t('finance.reports.totals'), tb.balanced ? t('finance.books.balanced') : t('finance.books.unbalanced'), ...TRIAL_COLUMNS.map((c) => fmtMoney(tb.totals[c]))],
        },
      },
    ],
  })
}

export type ReportKind = 'pnl' | 'banks' | 'trial-balance' | 'tax'

/** Kurumsal Excel/PDF sunucuda üretilir (dosya adı sunucudan: Urunver_<Rapor>_<Dönem>_<Tarih>). */
export const downloadReport = (kind: ReportKind, format: 'xlsx' | 'pdf', params: Record<string, string>) =>
  api.download(`/api/v1/finance/reports/${kind}/export`, `Urunver_${kind}.${format}`, { ...params, format })
